import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { journalApi } from '../../../api/journalApi';

/**
 * AttachmentUploader
 * Props:
 *   mode          - 'icons' | 'view' | 'edit' | 'create'
 *   entryId       - UUID of the journal entry (null if mode='create')
 *   token         - JWT
 *   attachments   - array of existing attachment objects
 *   onUpdate      - callback() when attachments change
 */
const AttachmentUploader = forwardRef(({ mode = 'view', entryId, token, attachments = [], onUpdate, onTranscriptGenerated }, ref) => {
    // All hooks must be at the top — no conditional returns before this block
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [recording, setRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [audioName, setAudioName] = useState('');
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [transcript, setTranscript] = useState('');
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [editingAttachmentId, setEditingAttachmentId] = useState(null);
    const [editAttachmentName, setEditAttachmentName] = useState('');
    const [isRenaming, setIsRenaming] = useState(false);
    const [showTranscriptFor, setShowTranscriptFor] = useState(null);
    const [transcriptDraft, setTranscriptDraft] = useState('');
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const [pendingFiles, setPendingFiles] = useState([]); // Array of { file, customName }

    const fileInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const cancelNextRecordingRef = useRef(false);

    useEffect(() => {
        let interval;
        if (recording) {
            interval = setInterval(() => setRecordingDuration(p => p + 1), 1000);
        } else {
            setRecordingDuration(0);
        }
        return () => clearInterval(interval);
    }, [recording]);

    const formatDuration = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    // Expose upload method for 'create' mode
    useImperativeHandle(ref, () => ({
        getPendingTranscript: () => {
            if (audioBlob && transcript) {
                let finalName = audioName.trim() || `Sprachnachricht_${new Date().toISOString().replace(/[:.]/g, '-')}`;
                if (!finalName.endsWith('.webm')) finalName += '.webm';
                return { name: finalName, text: transcript };
            }
            return null;
        },
        uploadPending: async (newEntryId) => {
            let uploadedSomething = false;

            if (pendingFiles.length > 0) {
                const formData = new FormData();
                for (const item of pendingFiles) {
                    // Ensure the file extension is kept if they renamed it without one
                    let finalName = item.customName.trim() || item.file.name;
                    if (!finalName.includes('.')) {
                        const ext = item.file.name.split('.').pop();
                        if (ext !== item.file.name) finalName += '.' + ext;
                    }
                    formData.append('files', item.file, finalName);
                }
                try {
                    await journalApi.addAttachments(newEntryId, formData);
                    uploadedSomething = true;
                } catch (e) {
                    console.error("Datei Upload Fehler", e);
                }
            }

            if (audioBlob) {
                const formData = new FormData();
                let finalName = audioName.trim() || `Sprachnachricht_${new Date().toISOString().replace(/[:.]/g, '-')}`;
                if (!finalName.endsWith('.webm')) finalName += '.webm';
                formData.append('files', audioBlob, finalName);
                if (transcript) formData.append('transcript', transcript);
                try {
                    await journalApi.addAttachments(newEntryId, formData);
                    uploadedSomething = true;
                } catch (e) {
                    console.error("Audio Upload Fehler", e);
                }
            }

            if (uploadedSomething && onUpdate) {
                onUpdate();
            }

            setPendingFiles([]);
            setAudioBlob(null);
            setAudioUrl(null);
            setAudioName('');
            setTranscript('');
        }
    }));

    // --- SHARED HELPERS ---
    const enqueueFiles = useCallback((files) => {
        if (!files || files.length === 0) return;
        const mapped = Array.from(files).map(f => {
            let previewUrl = null;
            if (f.type.startsWith('image/')) {
                previewUrl = URL.createObjectURL(f);
            }
            return { file: f, customName: f.name, previewUrl };
        });
        setPendingFiles(prev => [...prev, ...mapped]);
    }, []);

    // Cleanup preview URLs to avoid memory leaks
    useEffect(() => {
        return () => {
            pendingFiles.forEach(item => {
                if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            });
        };
    }, [pendingFiles]);

    const uploadFilesNow = useCallback(async () => {
        if (!entryId || pendingFiles.length === 0) return;

        setUploading(true);
        try {
            const formData = new FormData();
            for (const item of pendingFiles) {
                let finalName = item.customName.trim() || item.file.name;
                if (!finalName.includes('.')) {
                    const ext = item.file.name.split('.').pop();
                    if (ext !== item.file.name) finalName += '.' + ext;
                }
                formData.append('files', item.file, finalName);
            }
            await journalApi.addAttachments(entryId, formData);
            setPendingFiles([]);
            onUpdate && onUpdate();
        } catch (err) {
            alert('Upload fehlgeschlagen: ' + (err.response?.data?.error || err.message));
        } finally {
            setUploading(false);
        }
    }, [entryId, token, pendingFiles, onUpdate]);

    const handleDrop = useCallback((e) => { e.preventDefault(); setDragging(false); enqueueFiles(e.dataTransfer.files); }, [enqueueFiles]);

    const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
    const handleDragLeave = () => setDragging(false);

    // --- ICONS MODE: just small attachment indicators (early return AFTER all hooks) ---
    if (mode === 'icons') {
        if (!attachments || attachments.length === 0) return null;
        const hasImages = attachments.some(a => a.file_type === 'image' || a.file_type === 'video');
        const hasAudio = attachments.some(a => a.file_type === 'audio');
        const hasDocs = attachments.some(a => a.file_type === 'document');
        return (
            <span className="attachment-icons-row" title={`${attachments.length} Anhang/Anhänge`}>
                {hasImages && <span className="att-icon">📎</span>}
                {hasAudio && <span className="att-icon">🎙️</span>}
                {hasDocs && <span className="att-icon">📄</span>}
                <span className="att-count">{attachments.length}</span>
            </span>
        );
    }

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            chunksRef.current = [];
            const mr = new MediaRecorder(stream);
            mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = () => {
                stream.getTracks().forEach(t => t.stop());
                if (cancelNextRecordingRef.current) {
                    cancelNextRecordingRef.current = false;
                    return;
                }
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                setAudioBlob(blob);
                setAudioUrl(URL.createObjectURL(blob));
                setAudioName(`Sprachnachricht_${new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }).replace(':', '-')}`);
            };
            mediaRecorderRef.current = mr;
            mr.start();
            setRecording(true);
            setRecordingDuration(0);
        } catch (err) {
            alert('Mikrofon-Zugriff verweigert: ' + err.message);
        }
    };

    const stopRecording = () => { mediaRecorderRef.current?.stop(); setRecording(false); };

    const cancelRecording = () => {
        cancelNextRecordingRef.current = true;
        mediaRecorderRef.current?.stop();
        setRecording(false);
        setRecordingDuration(0);
    };

    const uploadAudio = async () => {
        if (!audioBlob) return;
        setUploading(true);
        try {
            const formData = new FormData();
            let finalName = audioName.trim() || `Sprachnachricht_${new Date().toISOString().replace(/[:.]/g, '-')}`;
            if (!finalName.endsWith('.webm')) finalName += '.webm';
            formData.append('files', audioBlob, finalName);
            if (transcript) formData.append('transcript', transcript);
            await journalApi.addAttachments(entryId, formData);
            setAudioBlob(null); setAudioUrl(null); setAudioName(''); setTranscript('');
            onUpdate && onUpdate();
        } catch (err) {
            alert('Upload fehlgeschlagen: ' + (err.response?.data?.error || err.message));
        } finally {
            setUploading(false);
        }
    };

    const deleteAttachment = async (attId) => {
        if (!window.confirm('Anhang wirklich löschen?')) return;
        try {
            await journalApi.deleteAttachment(entryId, attId);
            onUpdate && onUpdate();
        } catch (err) {
            alert('Fehler: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleRenameAttachment = async (attId, oldName) => {
        if (!editAttachmentName.trim() || editAttachmentName.trim() === oldName) {
            setEditingAttachmentId(null);
            return;
        }
        setIsRenaming(true);
        try {
            await journalApi.renameAttachment(entryId, attId, editAttachmentName.trim());

            setEditingAttachmentId(null);
            if (onUpdate) {
                // We pass the old and new name so the parent component can run text-replacements in the content
                onUpdate({ type: 'rename', oldName: oldName, newName: editAttachmentName.trim() });
            }
        } catch (err) {
            alert('Fehler beim Umbenennen: ' + (err.response?.data?.error || err.message));
        } finally {
            setIsRenaming(false);
        }
    };

    const handleTranscribe = async () => {
        if (!audioBlob) return;
        setIsTranscribing(true);
        try {
            const formData = new FormData();
            let finalName = audioName.trim() || `Sprachnachricht_${new Date().toISOString().replace(/[:.]/g, '-')}`;
            if (!finalName.endsWith('.webm')) finalName += '.webm';

            formData.append('audio', audioBlob, finalName);

            const res = await journalApi.transcribeAudio(formData);

            const generatedText = res.text;
            setTranscript(generatedText);
        } catch (err) {
            console.error(err);
            alert('Fehler bei der Transkription: ' + (err.response?.data?.error || err.message));
        } finally {
            setIsTranscribing(false);
        }
    };

    const saveTranscript = async (attId) => {
        try {
            await journalApi.updateTranscript(entryId, attId, transcriptDraft);
            setShowTranscriptFor(null);
            onUpdate && onUpdate();
        } catch (err) {
            alert('Fehler beim Speichern');
        }
    };

    const isEditMode = mode === 'edit' || mode === 'create';

    return (
        <div className="attachment-section">
            {/* Existing Attachments */}
            {attachments.length > 0 && (
                <div className="attachment-grid">
                    {attachments.map(att => (
                        <div key={att.id} className="attachment-item">
                            {att.file_type === 'image' ? (
                                <div className="attachment-image-wrap" style={{ textAlign: 'center' }}>
                                    <img
                                        src={att.public_url}
                                        alt={att.file_name}
                                        className="attachment-thumb"
                                        onClick={() => setLightboxUrl(att.public_url)}
                                    />
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px', wordBreak: 'break-all', padding: '0 4px' }}>
                                        {editingAttachmentId === att.id ? (
                                            <input
                                                autoFocus
                                                type="text"
                                                className="form-input"
                                                style={{ fontSize: '0.75rem', padding: '2px 4px', width: '100%', textAlign: 'center' }}
                                                value={editAttachmentName}
                                                onChange={e => setEditAttachmentName(e.target.value)}
                                                onBlur={() => handleRenameAttachment(att.id, att.file_name)}
                                                onKeyDown={e => { if (e.key === 'Enter') handleRenameAttachment(att.id, att.file_name); if (e.key === 'Escape') setEditingAttachmentId(null); }}
                                                disabled={isRenaming}
                                            />
                                        ) : (
                                            <span
                                                style={{ cursor: isEditMode ? 'pointer' : 'default' }}
                                                onClick={() => { if (isEditMode) { setEditingAttachmentId(att.id); setEditAttachmentName(att.file_name); } }}
                                                title={isEditMode ? "Klicken zum Umbenennen" : ""}
                                            >
                                                {att.file_name} {isEditMode && <span style={{ opacity: 0.5, fontSize: '0.65rem' }}>✏️</span>}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ) : att.file_type === 'video' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <video src={att.public_url} controls className="attachment-video" />
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', wordBreak: 'break-all', textAlign: 'center' }}>
                                        {editingAttachmentId === att.id ? (
                                            <input
                                                autoFocus
                                                type="text"
                                                className="form-input"
                                                style={{ fontSize: '0.75rem', padding: '2px 4px', width: '100%', textAlign: 'center' }}
                                                value={editAttachmentName}
                                                onChange={e => setEditAttachmentName(e.target.value)}
                                                onBlur={() => handleRenameAttachment(att.id, att.file_name)}
                                                onKeyDown={e => { if (e.key === 'Enter') handleRenameAttachment(att.id, att.file_name); if (e.key === 'Escape') setEditingAttachmentId(null); }}
                                                disabled={isRenaming}
                                            />
                                        ) : (
                                            <span
                                                style={{ cursor: isEditMode ? 'pointer' : 'default' }}
                                                onClick={() => { if (isEditMode) { setEditingAttachmentId(att.id); setEditAttachmentName(att.file_name); } }}
                                                title={isEditMode ? "Klicken zum Umbenennen" : ""}
                                            >
                                                {att.file_name} {isEditMode && <span style={{ opacity: 0.5, fontSize: '0.65rem' }}>✏️</span>}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ) : att.file_type === 'audio' ? (
                                <div className="attachment-audio-wrap">
                                    <div className="attachment-audio-label">
                                        <span>🎙️</span>
                                        {editingAttachmentId === att.id ? (
                                            <input
                                                autoFocus
                                                type="text"
                                                className="form-input"
                                                style={{ fontSize: '0.75rem', padding: '2px 4px', flex: 1 }}
                                                value={editAttachmentName}
                                                onChange={e => setEditAttachmentName(e.target.value)}
                                                onBlur={() => handleRenameAttachment(att.id, att.file_name)}
                                                onKeyDown={e => { if (e.key === 'Enter') handleRenameAttachment(att.id, att.file_name); if (e.key === 'Escape') setEditingAttachmentId(null); }}
                                                disabled={isRenaming}
                                            />
                                        ) : (
                                            <span
                                                className="attachment-name"
                                                style={{ cursor: isEditMode ? 'pointer' : 'default', flex: 1 }}
                                                onClick={() => { if (isEditMode) { setEditingAttachmentId(att.id); setEditAttachmentName(att.file_name); } }}
                                                title={isEditMode ? "Klicken zum Umbenennen" : ""}
                                            >
                                                {att.file_name} {isEditMode && <span style={{ opacity: 0.5, fontSize: '0.65rem' }}>✏️</span>}
                                            </span>
                                        )}
                                    </div>
                                    <audio src={att.public_url} controls className="attachment-audio" />
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px', backgroundColor: '#f1f5f9', borderRadius: '4px' }}>
                                    <span>📄</span>
                                    {editingAttachmentId === att.id ? (
                                        <input
                                            autoFocus
                                            type="text"
                                            className="form-input"
                                            style={{ fontSize: '0.75rem', padding: '2px 4px', flex: 1 }}
                                            value={editAttachmentName}
                                            onChange={e => setEditAttachmentName(e.target.value)}
                                            onBlur={() => handleRenameAttachment(att.id, att.file_name)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleRenameAttachment(att.id, att.file_name); if (e.key === 'Escape') setEditingAttachmentId(null); }}
                                            disabled={isRenaming}
                                        />
                                    ) : (
                                        <a href={att.public_url} target="_blank" rel="noreferrer" className="attachment-doc" style={{ padding: 0, backgroundColor: 'transparent', flex: 1 }}>
                                            <span
                                                style={{ cursor: isEditMode ? 'pointer' : 'pointer' }}
                                                onClick={(e) => {
                                                    if (isEditMode) {
                                                        e.preventDefault();
                                                        setEditingAttachmentId(att.id);
                                                        setEditAttachmentName(att.file_name);
                                                    }
                                                }}
                                                title={isEditMode ? "Klicken zum Umbenennen" : ""}
                                            >
                                                {att.file_name} {isEditMode && <span style={{ opacity: 0.5, fontSize: '0.65rem' }}>✏️</span>}
                                            </span>
                                        </a>
                                    )}
                                </div>
                            )}
                            {isEditMode && mode !== 'create' && (
                                <button className="attachment-delete-btn" onClick={() => deleteAttachment(att.id)} title="Löschen">×</button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Pending Files (Create & Edit Mode) */}
            {pendingFiles.length > 0 && (
                <div className="attachment-grid" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ width: '100%', marginBottom: '8px', fontWeight: 'bold', fontSize: '0.9rem', color: '#475569' }}>Vorbereitete Dateien ({pendingFiles.length}):</div>
                    {pendingFiles.map((item, idx) => (
                        <div key={idx} className="attachment-item" style={{ borderStyle: 'dashed', borderColor: '#94a3b8', backgroundColor: '#fff', padding: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {item.previewUrl && (
                                <div style={{ width: '100%', height: '80px', flexShrink: 0, textAlign: 'center', marginBottom: '4px' }}>
                                    <img src={item.previewUrl} alt="Vorschau" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px' }} />
                                </div>
                            )}
                            <div style={{ flex: 1 }}>
                                <input
                                    type="text"
                                    value={item.customName}
                                    onChange={(e) => {
                                        const newArr = [...pendingFiles];
                                        newArr[idx].customName = e.target.value;
                                        setPendingFiles(newArr);
                                    }}
                                    className="form-input"
                                    style={{ fontSize: '0.8rem', padding: '4px 6px', width: '100%' }}
                                    title="Name anpassen"
                                    placeholder="Dateiname..."
                                />
                                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px', wordBreak: 'break-all' }}>Original: {item.file.name}</div>
                            </div>
                            <button className="btn-cancel-small" onClick={() => {
                                setPendingFiles(prev => prev.filter((_, i) => i !== idx));
                                if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
                            }} title="Entfernen">Entfernen</button>
                        </div>
                    ))}
                    {mode === 'edit' && pendingFiles.length > 0 && (
                        <div style={{ width: '100%', marginTop: '4px', textAlign: 'right' }}>
                            <span style={{ color: '#059669', fontSize: '0.85rem', fontWeight: 500 }}>
                                ✓ Werden beim 'Speichern' hochgeladen
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Transcript Edit Modal */}
            {showTranscriptFor && (
                <div className="transcript-edit-overlay" onClick={() => setShowTranscriptFor(null)}>
                    <div className="transcript-edit-box" onClick={e => e.stopPropagation()}>
                        <h5>Transkript bearbeiten</h5>
                        <textarea
                            value={transcriptDraft}
                            onChange={e => setTranscriptDraft(e.target.value)}
                            rows={4}
                            placeholder="Inhalt der Sprachnachricht..."
                            className="transcript-textarea"
                        />
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                            <button className="btn-cancel-small" onClick={() => setShowTranscriptFor(null)}>Abbrechen</button>
                            <button className="btn-save-small" onClick={() => saveTranscript(showTranscriptFor)}>Speichern</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox */}
            {lightboxUrl && (
                <div className="lightbox-overlay" onClick={() => setLightboxUrl(null)}>
                    <img src={lightboxUrl} alt="Vollbild" className="lightbox-img" />
                    <button className="lightbox-close" onClick={() => setLightboxUrl(null)}>×</button>
                </div>
            )}

            {/* Upload Controls (edit and create mode) */}
            {isEditMode && (
                <>
                    {audioUrl && (
                        <div className="audio-preview-box">
                            <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>Aufnahme vorbereitet:</span>
                            <audio src={audioUrl} controls style={{ width: '100%', marginTop: '6px', marginBottom: '8px' }} />

                            <label style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '4px' }}>Dateiname anpassen:</label>
                            <input
                                type="text"
                                value={audioName}
                                onChange={e => setAudioName(e.target.value)}
                                className="form-input"
                                style={{ width: '100%', fontSize: '0.85rem', padding: '6px 8px', marginBottom: '10px' }}
                                placeholder="Name der Sprachnachricht..."
                            />

                            <textarea
                                value={transcript}
                                onChange={e => setTranscript(e.target.value)}
                                placeholder="Optionales Transkript eingeben..."
                                rows={2}
                                className="transcript-textarea"
                            />
                            <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'center' }}>
                                <button className="btn-cancel-small" onClick={() => { setAudioBlob(null); setAudioUrl(null); setAudioName(''); setTranscript(''); }}>Verwerfen</button>
                                <button
                                    className="side-menu-btn"
                                    onClick={handleTranscribe}
                                    disabled={isTranscribing}
                                    style={{ backgroundColor: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', padding: '4px 8px', fontSize: '0.8rem', height: 'auto' }}
                                >
                                    {isTranscribing ? '⏳ Erstelle Transkript...' : '🤖 Transkript erstellen'}
                                </button>
                                <span style={{ color: '#059669', fontSize: '0.85rem', marginLeft: 'auto', fontWeight: 500 }}>
                                    ✓ Wird beim Speichern hochgeladen
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="attachment-controls">
                        <div
                            className={`upload-drop-zone ${dragging ? 'dragging' : ''}`}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                type="file"
                                multiple
                                accept="image/*,video/*,audio/*,application/pdf"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={e => enqueueFiles(e.target.files)}
                            />
                            {uploading ? (
                                <span>⏳ Hochladen...</span>
                            ) : (
                                <span>📎 Dateien / Bilder / Videos auswählen</span>
                            )}
                        </div>

                        {recording ? (
                            <div style={{ display: 'flex', gap: '4px', flex: 1, minWidth: 0 }}>
                                <button
                                    className="btn-voice-record recording"
                                    onClick={stopRecording}
                                    type="button"
                                    title="Aufnahme abschließen"
                                    style={{ flex: 2 }}
                                >
                                    ⏹ {formatDuration(recordingDuration)}
                                </button>
                                <button
                                    className="btn-voice-record"
                                    onClick={cancelRecording}
                                    type="button"
                                    title="Aufnahme abbrechen"
                                    style={{ flex: 1, backgroundColor: '#fef2f2', color: '#ef4444', borderColor: '#fca5a5' }}
                                >
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <button
                                className="btn-voice-record"
                                onClick={startRecording}
                                disabled={uploading || !!audioUrl}
                                type="button"
                                title="Sprachnachricht aufnehmen"
                            >
                                🎙️
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
});

export default AttachmentUploader;
